import { Dialog, DIALOG_DATA } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import { LiveService } from '@app/core/services/live.service';
import { ModalRecordComponent } from '@app/modules/eclass/components/modal-record/modal-record.component';
import { ModalRecordingsJitsiComponent } from '@app/modules/eclass/components/modal-recordings-jitsi/modal-recordings-jitsi.component';
import { notNull, safeEmptyList } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';

import { Store } from '@ngrx/store';
import { finalize, forkJoin, map, of, switchMap, take } from 'rxjs';
import { LinkType } from '@app/core/models/link-type.enum';
import { Subject } from '@app/core/models/academic.model';

enum ControlOptions {
  Finalize = '3',
  Open = '4',
  Close = '5',
  Restrict = '1',
  Unrestrict = '2',
  RecordingList = '6',
  AutoRecording = '11',
  ManualRecording = '12',
}

interface Live {
  id: number;
  turma_id: number;
  usuario_id: number;
  entidade_id: number;
  restrito: number;
  link: string;
  date_start: string;
  date_end: string;
  students?: Array<{ id: number }>;
  descricao?: string;
  codigo?: string;
  title?: string;
  subject?: Subject;
  pin?: boolean;
}

interface JitsiLive {
  id?: number;
  descricao: string;
  codigo: string;
  turma_id: number;
  entidade_id: number;
  usuario_id: number;
  link: string;
}

@Component({
  selector: 'app-modal-lives',
  templateUrl: './modal-lives.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ModalLivesComponent {
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _router = inject(Router);
  private readonly _dialog = inject(Dialog);
  private readonly _liveService = inject(LiveService);
  private readonly _destroyRef = inject(DestroyRef);
  public readonly lives = inject(DIALOG_DATA);
  public readonly loading = signal(true);

  public livesZoom: Live[] = [];
  public livesMeet: Live[] = [];
  public livesJitsi: JitsiLive[] = [];

  public readonly academicYear = toSignal(
    this._store.select(AppSelectors.ActiveUser).pipe(
      notNull(),
      map(({ ano_letivo }) => ano_letivo),
    ),
  );

  constructor() {
    this.loadLives();
  }

  private loadLives(): void {
    this.loading.set(true);
    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        notNull(),
        take(1),
        switchMap((activeUser) =>
          forkJoin({
            zoom: this.loadZoomLives(activeUser),
            meet: this.loadMeetLives(activeUser),
            jitsi: of(this.buildJitsiLivesFromActiveUser(activeUser)),
          }),
        ),
        finalize(() => this.loading.set(false)),
      )
      .subscribe({
        next: ({ zoom, meet, jitsi }) => {
          this.livesZoom = zoom;
          this.livesMeet = meet;
          this.livesJitsi = jitsi;
        },
        error: () => {
          this.livesZoom = [];
          this.livesMeet = [];
          this.livesJitsi = [];
        },
      });
  }

  public openHall(live: any) {
    this._liveService
      .controlAccessLive('17', live.turma_id, live.usuario_id)
      .pipe(take(1))
      .subscribe((response) => {
        this.openExternalLink(response?.object);
      });
  }

  public openLive(link: string): void {
    this.openExternalLink(link);
  }

  public onLoadRecords(groupId: number, groupName: string): void {
    this._dialog.open(ModalRecordComponent, {
      data: {
        groupId,
        groupName,
        controlOptions: ControlOptions,
      },
      width: '70vw',
    });
  }

  public onLoadRecordingsJitsi(live: any): void {
    const turmaId = live.turma_id ?? live.id;

    this._store
      .select(AppSelectors.ActiveUser)
      .pipe(
        notNull(),
        map((user) => user.entidade_id),
        take(1),
      )
      .subscribe((entidadeId) => {
        const path = `recordings/${this.academicYear()}/${entidadeId}/${turmaId}/`;
        this._dialog.open(ModalRecordingsJitsiComponent, {
          data: { path },
          width: '70vw',
        });
      });
  }

  private loadZoomLives(activeUser: any) {
    if (this.lives?.zoom) {
      return of(this.lives.zoom);
    }
    const { entidade_id, usuario_id } = activeUser;

    return this._liveService
      .getLivesStudent({
        entidade_id,
        usuario_id,
      })
      .pipe(
        safeEmptyList<Live>(),
        map((lives) =>
          lives.map((live) => ({
            ...live,
            usuario_id,
          })),
        ),
      );
  }

  private loadMeetLives(activeUser: any) {
    const { aluno_id } = activeUser;

    return this._liveService.getLivesMeet(LinkType.MeetAcademico).pipe(
      safeEmptyList<Live>(),
      map((lives) => lives.filter((live) => this.isAvailableMeetLive(live, aluno_id))),
    );
  }

  private isAvailableMeetLive(live: Live, alunoId: number): boolean {
    const now = new Date();

    const isStudentAllowed = !live.students?.length || live.students.some((student) => student.id === alunoId);

    const isWithinLivePeriod = new Date(live.date_start) <= now && new Date(live.date_end) >= now;

    return isStudentAllowed && isWithinLivePeriod;
  }

  private buildJitsiLivesFromActiveUser(activeUser: any): JitsiLive[] {
    const entidadeId = Number(activeUser?.entidade_id);
    const usuarioId = Number(activeUser?.usuario_id);

    const turmaIds = this.getTurmaIds(activeUser?.turma_id);

    if (!entidadeId || !turmaIds.length) {
      return [];
    }

    return turmaIds.map((turmaId, index) => {
      const descricao = this.getTurmaDescription(activeUser?.serie, index, turmaId);

      return {
        descricao,
        codigo: descricao,
        turma_id: turmaId,
        entidade_id: entidadeId,
        usuario_id: usuarioId,
        link: this.buildJitsiRoomAbsoluteUrl(turmaId),
      };
    });
  }

  private getTurmaIds(turmaId: number | number[] | undefined): number[] {
    const turmaIds = Array.isArray(turmaId) ? turmaId : [turmaId];

    return Array.from(new Set(turmaIds.map(Number).filter(Boolean)));
  }

  private getTurmaDescription(turmaDescription: string | string[] | undefined, index: number, turmaId: number): string {
    const description = Array.isArray(turmaDescription)
      ? String(turmaDescription[index] ?? '')
      : String(turmaDescription ?? '');

    return description || `${turmaId}`;
  }

  private buildJitsiRoomAbsoluteUrl(turmaId: number): string {
    const path = this._router.serializeUrl(this._router.createUrlTree(['/lives', turmaId]));

    return `${window.location.origin}${path}`;
  }

  private openExternalLink(link?: string): void {
    if (!link) {
      return;
    }
    window.open(link, '_blank');
  }
}
